class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if(input){
      input.value = input.getAttribute('value');
      this.isEnterPressed = false;
    }
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          
          // Publish mini cart update event after DOM update
          publish(PUB_SUB_EVENTS.cartUpdateInMiniCart, {
            source: 'cart-drawer-items',
            cartData: null
          });
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
          
          // Publish mini cart update event after DOM update
          publish(PUB_SUB_EVENTS.cartUpdateInMiniCart, {
            source: 'cart-items',
            cartData: null
          });
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then(async (state) => {
        const parsedState = JSON.parse(state);

        // Variable to track if we need to refresh (set inside measure, used outside)
        let partialUpdateData = null;

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            
            // Check if this is a partial update (422 with items modified) or complete failure
            // Shopify returns "Only X items were added to your cart due to availability." in errors field
            const errorMessage = typeof parsedState.errors === 'string' ? parsedState.errors : '';
            
            const isPartialUpdate = errorMessage.includes('were added') || 
                                   errorMessage.includes('items were added') || 
                                   errorMessage.includes('due to availability');
            
            
            if (isPartialUpdate) {
              
              // Items were partially updated - store data for refresh outside measure block
              partialUpdateData = { errorMessage: parsedState.errors };
              return;
            }
            
          
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          const currentItemData = parsedState.items[line - 1];
          const isFreeItem = currentItemData && (currentItemData.final_price === 0 || currentItemData.price === 0);
          const isSwatch = currentItemData && currentItemData.product_type === 'Swatches';
          const isFreeSwatch = isSwatch && isFreeItem;
          const requestedQty = parseInt(quantityElement.value);
          
          let message = '';
          // Check if this is a free swatch that got reverted (user requested more but it stayed same)
          const freeSwatchReverted = isFreeSwatch && updatedValue !== undefined && requestedQty > updatedValue;
          
          // Skip message for free items (like free swatches) unless we need to show for both later
          //console.log('[Cart Debug - Block A] line:', line, 'isFreeItem:', isFreeItem, 'items.length:', items.length, 'parsedState.items.length:', parsedState.items.length, 'updatedValue:', updatedValue, 'requestedQty:', requestedQty);
          if (!isFreeItem && items.length === parsedState.items.length && updatedValue !== requestedQty) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
              //console.log('[Cart Debug - Block A1] Setting message to error string');
            } else {
              if (requestedQty > updatedValue) {
                message = window.cartStrings.maxQuantityError;
                //console.log('[Cart Debug - Block A2] Setting message: Max available - requestedQty > updatedValue');
              } else {
                message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
                //console.log('[Cart Debug - Block A3] Setting message: quantityError');
              }
            }
          }
          //console.log('[Cart Debug - Block A Final] Calling updateLiveRegions with line:', line, 'message:', message);
          this.updateLiveRegions(line, message);
          
          // Check ALL items for quantity reductions (for cases like free swatch triggering paid swatch change)
          // Compare old DOM values with new response values
          let hasPaidSwatchAtMax = false;
          
          // Check if new line items were created (items split, not reduced)
          const newLineItemsCreated = parsedState.items.length > items.length;
          
          items.forEach((item, index) => {
            const itemInput = item.querySelector('input[name="updates[]"]') || item.querySelector('.quantity__input');
            if (!itemInput) return;
            
            const itemLine = parseInt(itemInput.dataset.index || (index + 1), 10);
            const oldQty = parseInt(itemInput.value, 10);
            const newItemData = parsedState.items[itemLine - 1];
            
            // Skip if no item data
            if (!newItemData) return;
            
            // Check if this is a paid swatch
            const itemIsSwatch = newItemData.product_type === 'Swatches';
            const itemIsPaid = newItemData.final_price > 0 && newItemData.price > 0;
            const isPaidSwatch = itemIsSwatch && itemIsPaid;
            const newQty = newItemData.quantity;
            
            // If paid swatch quantity was reduced (not by user action on this line), show message
            // BUT skip if new line items were created (items were split, not truly reduced)
            if (isPaidSwatch && oldQty > newQty && itemLine !== line && !newLineItemsCreated) {
              //console.log('[Cart Debug - Block B] Paid swatch qty reduced. itemLine:', itemLine, 'oldQty:', oldQty, 'newQty:', newQty);
              this.updateLiveRegions(itemLine, window.cartStrings.maxQuantityError);
              hasPaidSwatchAtMax = true;
            }
            
            // If paid swatch quantity stayed same when free swatch was increased (max reached)
            // Also skip if new line items were created
            if (isPaidSwatch && freeSwatchReverted && oldQty === newQty && !newLineItemsCreated) {
              //console.log('[Cart Debug - Block C] Paid swatch at max, free swatch reverted. itemLine:', itemLine);
              hasPaidSwatchAtMax = true;
            }
          });
          
          // If free swatch was reverted AND there's a paid swatch at max, show message on free swatch too
          if (freeSwatchReverted && hasPaidSwatchAtMax) {
            //console.log('[Cart Debug - Block D] Showing message on free swatch line:', line);
            this.updateLiveRegions(line, window.cartStrings.maxQuantityError);
          }

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        // Handle partial update - fetch fresh cart sections
        if (partialUpdateData) {
          
          try {
            // Fetch fresh cart data and sections
            // Use section property if available, otherwise use id
            const sectionsToFetch = this.getSectionsToRender()
              .map((section) => section.section || section.id)
              .filter(Boolean)
              .join(',');
            
            
            
            const [cartResponse, sectionsResponse] = await Promise.all([
              fetch('/cart.js').then(r => r.json()),
              fetch(`${window.location.pathname}?sections=${sectionsToFetch}`).then(r => r.json())
            ]);

            
            // Update cart drawer or cart page
            this.classList.toggle('is-empty', cartResponse.item_count === 0);
            const cartDrawerWrapper = document.querySelector('cart-drawer');
            const cartFooter = document.getElementById('main-cart-footer');

            if (cartFooter) cartFooter.classList.toggle('is-empty', cartResponse.item_count === 0);
            if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', cartResponse.item_count === 0);

            // Update each section
            this.getSectionsToRender().forEach((section) => {
              const sectionKey = section.section || section.id;
              if (sectionsResponse[sectionKey]) {
                // Match original pattern: find by ID, then querySelector for selector, or fallback to ID element
                const parentElement = document.getElementById(section.id);
                const elementToReplace = (parentElement && section.selector) 
                  ? (parentElement.querySelector(section.selector) || parentElement)
                  : parentElement;
                if (elementToReplace) {
                  elementToReplace.innerHTML = this.getSectionInnerHTML(
                    sectionsResponse[sectionKey],
                    section.selector || '.shopify-section'
                  );
                }
              }
            });

            // Show the error message to user with clear inventory limit info
            if (partialUpdateData.errorMessage) {
              // Get the actual quantity from refreshed cart
              const currentItem = cartResponse.items?.find(item => String(item.variant_id) === String(variantId));
              const actualQty = currentItem ? currentItem.quantity : null;
              //console.log('[Cart Debug - Block E] Partial update handler. variantId:', variantId, 'actualQty:', actualQty, 'errorMessage:', partialUpdateData.errorMessage);
              
              let displayMessage = partialUpdateData.errorMessage;
              if (actualQty !== null) {
                displayMessage = window.cartStrings.maxQuantityError;
                //console.log('[Cart Debug - Block E1] Setting displayMessage to max available');
              }
              //console.log('[Cart Debug - Block E Final] Calling updateLiveRegions with line:', line, 'displayMessage:', displayMessage);
              
              // Use setTimeout to ensure message displays after any DOM updates complete
              const self = this;
              const lineNum = line;
              //setTimeout(() => {
                //console.log('[Cart] Setting message after delay for line:', lineNum);
                self.updateLiveRegions(lineNum, displayMessage);
              //}, 100);
            }

            // Publish cart update events with fresh cart data
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cartResponse, variantId: variantId });
            publish(PUB_SUB_EVENTS.cartUpdateInMiniCart, {
              source: 'cart-items',
              cartData: cartResponse,
              variantId: variantId
            });
          } catch (e) {
            console.error('Failed to fetch cart sections after partial update:', e);
          }
          return;
        }

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        
        // Publish mini cart specific update event
        publish(PUB_SUB_EVENTS.cartUpdateInMiniCart, {
          source: 'cart-items',
          cartData: parsedState,
          variantId: variantId
        });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    //console.log('[Cart] updateLiveRegions - line:', line, 'element found:', lineItemError ? lineItemError.id : 'NONE', 'message:', message);
    if (lineItemError) {
      lineItemError.querySelector('.cart-item__error-text').textContent = message;
      // Show/hide the error element based on whether there's a message
      if (message) {
        lineItemError.style.display = 'flex';
        lineItemError.style.background = 'transparent';
        lineItemError.style.color = 'black';
        lineItemError.style.border = 'none';
        lineItemError.style.paddingLeft = '0';
      } else {
        lineItemError.style.display = 'none';
      }
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
